
require("./../../../node_modules/@module-federation/nextjs-mf/src/include-defaults.js");
import { useEffect } from 'react';

import ProductDetailPage from 'productTemplate/components/ProductDetailPage';
import { bugsnag } from 'shared/utils/services/bugsnag';
import { FeatureFlags } from 'featureFlags/constants';
import { getPdpServerSideProps } from 'shared/utils/getPdpServerSideProps';
import {
  IConsumerSearchFactsWindowData,
  IRelatedProductData,
} from 'productTemplate/types/FactsWindowData';
import { IFeaturedInCollection } from 'productTemplate/types/FeaturedInCollection';
import { IGmcOffers } from 'shared/types/Offer';
import { IProductTemplate } from 'shared/types/ProductTemplate';
import { ISizesMap } from 'productTemplate/types/SizesMap';
import { LOCALES } from 'localization/constants/configEnums';
import { requestService } from 'shared/utils/services/requestService';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { useLocation } from 'shared/hooks/useLocation';
import { Error } from 'error/components/Error';

interface IProductDetailPage {
  buyBarSizesMap: ISizesMap;
  country: string;
  factsWindowData: IConsumerSearchFactsWindowData;
  featuredInCollections: IFeaturedInCollection[];
  isCrawler: boolean;
  errorCode?: number;
  errorMessage?: string;
  locale?: LOCALES;
  offers: IGmcOffers;
  productTemplate: IProductTemplate;
  recommendedProducts: Array<IRelatedProductData>;
}

const ApparelPDP = ({
  buyBarSizesMap,
  country,
  factsWindowData,
  featuredInCollections,
  isCrawler = false,
  errorCode,
  errorMessage,
  locale,
  offers,
  productTemplate,
  recommendedProducts,
}: IProductDetailPage) => {
  const { selectedCountryCode } = useLocation();
  const isEnableUnavailableInRegion = useFeatureFlag(
    FeatureFlags.TEMP_WEB_ENABLE_UNAVAILABLE_IN_REGION,
  );
  const disableRefetchForUnavailableRegion = useFeatureFlag(
    FeatureFlags.TEMP_WEB_DISABLE_REFETCH_FOR_UNAVAILABLE_REGION,
  );
  let refetchedProductTemplate: IProductTemplate;

  // TODO: see if we can remove this since we are not caching PDP, remove disableRefetchForUnavailableRegion after testing in production
  useEffect(() => {
    const refetchProductTemplate = async () => {
      try {
        const pt: IProductTemplate = await requestService({
          url: `/web-api/v1/product_templates/${productTemplate?.slug}?countryCode=${country}`,
          method: 'GET',
        });
        refetchedProductTemplate = pt;
      } catch (error) {
        bugsnag(error, { context: 'Refetching Product Template' });
      }
    };

    // does not update and refetch product template every time you change shopping regions,
    // so adding a refetch here if the countries are different
    if (
      !disableRefetchForUnavailableRegion &&
      isEnableUnavailableInRegion &&
      !isCrawler &&
      country !== selectedCountryCode
    ) {
      void refetchProductTemplate();
    }
  }, [country, isCrawler, productTemplate, selectedCountryCode, isEnableUnavailableInRegion]);

  if (errorCode) {
    return <Error errorCode={errorCode} errorMessage={errorMessage} />;
  }

  return (
    <ProductDetailPage
      buyBarSizesMap={buyBarSizesMap}
      factsWindowData={factsWindowData}
      featuredInCollections={featuredInCollections}
      isCrawler={isCrawler}
      offers={offers}
      productTemplate={refetchedProductTemplate || productTemplate}
      recommendedProducts={recommendedProducts}
      locale={locale}
    />
  );
};

export async function getServerSideProps({ query, req, res, locale }) {
  return getPdpServerSideProps({ query, req, res, locale });
}

export default ApparelPDP;
